// src/alicuotas.ts
import { Porcentaje } from "@actualgold/shared";
function alicuotaQueCorresponde(paquete, codigo) {
  return codigo !== null && codigo !== void 0 && Object.hasOwn(paquete.alicuotas, codigo) ? codigo : paquete.alicuotaGeneral;
}
function esAlicuotaDelPais(paquete, codigo) {
  return Object.hasOwn(paquete.alicuotas, codigo);
}
function tasaDeAlicuotaDelPais(paquete, codigo) {
  return Porcentaje.desdeTexto(paquete.alicuotas[alicuotaQueCorresponde(paquete, codigo)] ?? "0");
}
function tasaDeAlicuota(paquetes, codigo) {
  for (const paquete of paquetes) {
    const tasa = paquete.alicuotas[codigo];
    if (tasa !== void 0) return Porcentaje.desdeTexto(tasa);
  }
  return void 0;
}

// src/ar/comprobantes.ts
import { CodigoDeError, ErrorDeDominio } from "@actualgold/shared";
var TIPOS_DE_COMPROBANTE = {
  A: { factura: 1, notaDeDebito: 2, notaDeCredito: 3 },
  B: { factura: 6, notaDeDebito: 7, notaDeCredito: 8 },
  C: { factura: 11, notaDeDebito: 12, notaDeCredito: 13 }
};
var CONDICION_IVA_DEL_RECEPTOR = {
  responsable_inscripto: 1,
  exento: 4,
  consumidor_final: 5,
  monotributo: 6,
  no_categorizado: 7
};
function claseQueCorresponde(emisor, receptor = "consumidor_final") {
  switch (emisor) {
    case "responsable_inscripto":
      return receptor === "responsable_inscripto" || receptor === "monotributo" ? "A" : "B";
    case "monotributo":
    case "exento":
    case "no_responsable":
      return "C";
    default:
      throw new ErrorDeDominio(CodigoDeError.ERR_CONDICION_IVA_NO_EMITE, { condicion: emisor });
  }
}
var LEYENDA_PARA_MONOTRIBUTISTA = "El cr\xE9dito fiscal discriminado en el presente comprobante, s\xF3lo podr\xE1 ser computado a efectos del R\xE9gimen de Sostenimiento e Inclusi\xF3n Fiscal para Peque\xF1os Contribuyentes de la Ley N\xBA 27.618";
function llevaLeyendaDeMonotributo(clase, receptor) {
  return clase === "A" && receptor === "monotributo";
}
function discriminaIva(clase) {
  return clase === "A";
}
var LEYENDA_DEL_EMISOR = {
  responsable_inscripto: "IVA RESPONSABLE INSCRIPTO",
  exento: "IVA EXENTO",
  no_responsable: "NO RESPONSABLE IVA",
  monotributo: "RESPONSABLE MONOTRIBUTO",
  // Las dos que siguen no pueden emitir: `claseQueCorresponde` las rechaza antes. Están para que
  // el tipo quede completo y no para imprimirse.
  consumidor_final: "CONSUMIDOR FINAL",
  no_categorizado: "SUJETO NO CATEGORIZADO"
};
var LEYENDA_DEL_RECEPTOR = {
  responsable_inscripto: "IVA RESPONSABLE INSCRIPTO",
  exento: "IVA EXENTO",
  consumidor_final: "A CONSUMIDOR FINAL",
  monotributo: "RESPONSABLE MONOTRIBUTO",
  no_categorizado: "SUJETO NO CATEGORIZADO"
};
var TITULO_DE_TRANSPARENCIA_FISCAL = "R\xE9gimen de Transparencia Fiscal al Consumidor (Ley 27.743)";
var DATO_DE_IVA_CONTENIDO = "IVA Contenido";
var DATO_DE_OTROS_IMPUESTOS = "Otros Impuestos Nacionales Indirectos";
function llevaTransparenciaFiscal(emisor, clase) {
  return emisor === "responsable_inscripto" && clase === "B";
}
function numeroDeComprobante(puntoDeVenta, numero2) {
  return `${String(puntoDeVenta).padStart(4, "0")}-${String(numero2).padStart(8, "0")}`;
}

// src/ar/identificacion.ts
var PESOS = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
function soloDigitos(valor) {
  return valor.replace(/\D/g, "");
}
function digitoVerificadorDeCuit(primerosDiez) {
  let suma = 0;
  for (let i = 0; i < PESOS.length; i += 1) {
    suma += Number(primerosDiez[i] ?? "0") * (PESOS[i] ?? 0);
  }
  const resto = suma % 11;
  const verificador = 11 - resto;
  if (verificador === 11) return 0;
  if (verificador === 10) return -1;
  return verificador;
}
function validarCuit(valor) {
  const digitos = soloDigitos(valor);
  if (digitos.length !== 11) return { valido: false, motivo: "formato" };
  const esperado = digitoVerificadorDeCuit(digitos.slice(0, 10));
  if (esperado === -1 || esperado !== Number(digitos[10])) {
    return { valido: false, motivo: "digito_verificador" };
  }
  return { valido: true };
}
function validarDni(valor) {
  const digitos = soloDigitos(valor);
  return digitos.length >= 7 && digitos.length <= 8 ? { valido: true } : { valido: false, motivo: "formato" };
}
var IDENTIFICACION_ARGENTINA = {
  tiposDeIdentificacion: ["CUIT", "CUIL", "DNI", "PASAPORTE", "OTRO"],
  normalizarIdentificacion(valor) {
    return soloDigitos(valor);
  },
  validarIdentificacion(tipo, valor) {
    switch (tipo) {
      case "CUIT":
      case "CUIL":
        return validarCuit(valor);
      case "DNI":
        return validarDni(valor);
      case "PASAPORTE":
      case "OTRO":
        return valor.trim().length > 0 ? { valido: true } : { valido: false, motivo: "formato" };
      default:
        return { valido: false, motivo: "tipo_no_admitido" };
    }
  },
  formatearIdentificacion(tipo, valor) {
    const digitos = soloDigitos(valor);
    if ((tipo === "CUIT" || tipo === "CUIL") && digitos.length === 11) {
      return `${digitos.slice(0, 2)}-${digitos.slice(2, 10)}-${digitos.slice(10)}`;
    }
    if (tipo === "DNI" && digitos.length >= 7) {
      return new Intl.NumberFormat("es-AR").format(Number(digitos));
    }
    return valor.trim();
  }
};

// src/ar/impuestos.ts
import { Porcentaje as Porcentaje2 } from "@actualgold/shared";
var ALICUOTAS_DE_IVA = {
  "0003": "0.0000",
  "0004": "10.5000",
  "0005": "21.0000",
  "0006": "27.0000",
  "0008": "5.0000",
  "0009": "2.5000"
};
var CODIGOS_DE_ALICUOTA = ["0003", "0004", "0005", "0006", "0008", "0009"];
var ALICUOTA_GENERAL = "0005";
function esCodigoDeAlicuota(valor) {
  return Object.hasOwn(ALICUOTAS_DE_IVA, valor);
}
function alicuotaDe(codigo) {
  return Porcentaje2.desdeTexto(ALICUOTAS_DE_IVA[codigo]);
}

// src/ar/paquete.ts
var CONDICIONES_DEL_EMISOR = [
  "responsable_inscripto",
  "monotributo",
  "exento",
  "no_responsable"
];
var CONDICIONES_DEL_RECEPTOR = [
  "responsable_inscripto",
  "monotributo",
  "exento",
  "consumidor_final",
  "no_categorizado"
];
var esCondicionDelReceptor = (valor) => CONDICIONES_DEL_RECEPTOR.includes(valor);
var ARGENTINA = {
  ...IDENTIFICACION_ARGENTINA,
  codigo: "AR",
  monedaPorDefecto: "ARS",
  zonaHoraria: "America/Argentina/Buenos_Aires",
  tiposDeIdentificacionDeEmpresa: ["CUIT", "CUIL", "DNI", "PASAPORTE", "OTRO"],
  tiposDeIdentificacionDeTercero: ["CUIT", "CUIL", "DNI", "PASAPORTE", "SIN_IDENTIFICAR"],
  ejemploDeIdentificacion: "30-71234567-1",
  condicionesDelEmisor: CONDICIONES_DEL_EMISOR,
  condicionesDelReceptor: CONDICIONES_DEL_RECEPTOR,
  condicionDelReceptorPorOmision: "consumidor_final",
  alicuotas: ALICUOTAS_DE_IVA,
  alicuotaGeneral: ALICUOTA_GENERAL,
  claseDelComprobante(emisor, receptor) {
    return claseQueCorresponde(
      emisor,
      receptor !== void 0 && esCondicionDelReceptor(receptor) ? receptor : "consumidor_final"
    );
  },
  tieneFiscoElectronico: true
};

// src/comun.ts
function soloDigitos2(valor) {
  return valor.replace(/\D/g, "");
}
function conPuntos(digitos) {
  return digitos.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}
function algoEscrito(valor) {
  return valor.trim().length > 0 ? { valido: true } : { valido: false, motivo: "formato" };
}

// src/cl/paquete.ts
function digitoVerificadorDeRut(cuerpo) {
  let suma = 0;
  let factor = 2;
  for (let i = cuerpo.length - 1; i >= 0; i -= 1) {
    suma += Number(cuerpo[i]) * factor;
    factor = factor === 7 ? 2 : factor + 1;
  }
  const resultado = 11 - suma % 11;
  if (resultado === 11) return "0";
  if (resultado === 10) return "K";
  return String(resultado);
}
function normalizar(valor) {
  return valor.toUpperCase().replace(/[^0-9K]/g, "");
}
function validarRut(valor) {
  const rut = normalizar(valor);
  if (!/^\d{7,8}[0-9K]$/.test(rut)) return { valido: false, motivo: "formato" };
  return digitoVerificadorDeRut(rut.slice(0, -1)) === rut.slice(-1) ? { valido: true } : { valido: false, motivo: "digito_verificador" };
}
var CHILE = {
  codigo: "CL",
  // Peso chileno, sin decimales: ya sembrado así en `monedas`.
  monedaPorDefecto: "CLP",
  zonaHoraria: "America/Santiago",
  tiposDeIdentificacion: ["RUT", "PASAPORTE", "OTRO"],
  tiposDeIdentificacionDeEmpresa: ["RUT"],
  tiposDeIdentificacionDeTercero: ["RUT", "PASAPORTE", "SIN_IDENTIFICAR"],
  ejemploDeIdentificacion: "76.123.456-0",
  normalizarIdentificacion(valor) {
    return normalizar(valor);
  },
  validarIdentificacion(tipo, valor) {
    switch (tipo) {
      case "RUT":
        return validarRut(valor);
      case "PASAPORTE":
      case "OTRO":
        return algoEscrito(valor);
      default:
        return { valido: false, motivo: "tipo_no_admitido" };
    }
  },
  formatearIdentificacion(tipo, valor) {
    const rut = normalizar(valor);
    if (tipo === "RUT" && rut.length >= 2) {
      return `${conPuntos(rut.slice(0, -1))}-${rut.slice(-1)}`;
    }
    return valor.trim();
  },
  // SUPUESTO: los regímenes de la Ley sobre Impuesto a la Renta tras la Ley 21.210 —general
  // semi integrado (art. 14 A), Pro Pyme general (14 D N° 3) y Pro Pyme transparente (14 D N° 8),
  // según la Circular SII 62/2020—, más el comercio exento de IVA. Parten de las que usa
  // ActualHotel; falta que las confirme alguien que trabaje con comercios chilenos.
  condicionesDelEmisor: ["regimen_general", "pro_pyme_general", "pro_pyme_transparente", "exento"],
  condicionesDelReceptor: ["contribuyente", "consumidor_final", "exento"],
  condicionDelReceptorPorOmision: "consumidor_final",
  // Decreto Ley 825, artículo 14: la tasa del IVA es 19 %.
  alicuotas: { IVA19: "19.0000", EXENTO: "0.0000" },
  alicuotaGeneral: "IVA19",
  // Chile no tiene letras: factura y boleta son tipos de documento del SII, que llegan con su
  // factura electrónica.
  claseDelComprobante: () => null,
  tieneFiscoElectronico: false
};

// src/py/paquete.ts
function digitoVerificadorDeRuc(base) {
  let suma = 0;
  for (let i = 0; i < base.length; i += 1) {
    suma += Number(base[base.length - 1 - i]) * (i + 2);
  }
  const resto = suma % 11;
  return resto > 1 ? 11 - resto : 0;
}
function validarRuc(valor) {
  const ruc = soloDigitos2(valor);
  if (ruc.length < 2 || ruc.length > 9) return { valido: false, motivo: "formato" };
  return digitoVerificadorDeRuc(ruc.slice(0, -1)) === Number(ruc.slice(-1)) ? { valido: true } : { valido: false, motivo: "digito_verificador" };
}
function validarCedula(valor) {
  const cedula = soloDigitos2(valor);
  return cedula.length >= 4 && cedula.length <= 8 ? { valido: true } : { valido: false, motivo: "formato" };
}
var PARAGUAY = {
  codigo: "PY",
  // Guaraní, sin decimales: ya sembrado así en `monedas`.
  monedaPorDefecto: "PYG",
  zonaHoraria: "America/Asuncion",
  tiposDeIdentificacion: ["RUC", "CI", "PASAPORTE", "OTRO"],
  tiposDeIdentificacionDeEmpresa: ["RUC"],
  tiposDeIdentificacionDeTercero: ["RUC", "CI", "PASAPORTE", "SIN_IDENTIFICAR"],
  ejemploDeIdentificacion: "80028061-0",
  normalizarIdentificacion(valor) {
    return soloDigitos2(valor);
  },
  validarIdentificacion(tipo, valor) {
    switch (tipo) {
      case "RUC":
        return validarRuc(valor);
      case "CI":
        return validarCedula(valor);
      case "PASAPORTE":
      case "OTRO":
        return algoEscrito(valor);
      default:
        return { valido: false, motivo: "tipo_no_admitido" };
    }
  },
  formatearIdentificacion(tipo, valor) {
    const digitos = soloDigitos2(valor);
    if (tipo === "RUC" && digitos.length >= 2)
      return `${digitos.slice(0, -1)}-${digitos.slice(-1)}`;
    if (tipo === "CI" && digitos.length > 0) return conPuntos(digitos);
    return valor.trim();
  },
  // SUPUESTO: los tres regímenes del Impuesto a la Renta Empresarial de la Ley 6380/2019, con los
  // códigos de obligación de la DNIT: 700 general, 701 SIMPLE y 702 RESIMPLE. Falta que las
  // confirme alguien que trabaje con comercios paraguayos.
  condicionesDelEmisor: ["ire_general", "ire_simple", "ire_resimple"],
  condicionesDelReceptor: ["contribuyente", "consumidor_final", "exento"],
  condicionDelReceptorPorOmision: "consumidor_final",
  // Ley 6380/2019, artículo 90: tasa general del 10 % y reducida del 5 %.
  alicuotas: { IVA10: "10.0000", IVA5: "5.0000", EXENTO: "0.0000" },
  alicuotaGeneral: "IVA10",
  claseDelComprobante: () => null,
  tieneFiscoElectronico: false
};

// src/uy/paquete.ts
var PESOS_DEL_RUT = [4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
var PESOS_DE_LA_CEDULA = [2, 9, 8, 7, 6, 3, 4];
function digitoVerificadorDeRut2(primerosOnce) {
  let suma = 0;
  PESOS_DEL_RUT.forEach((peso, i) => {
    suma += Number(primerosOnce[i]) * peso;
  });
  return (11 - suma % 11) % 11;
}
function validarRut2(valor) {
  const rut = soloDigitos2(valor);
  if (rut.length !== 12) return { valido: false, motivo: "formato" };
  const registro = Number(rut.slice(0, 2));
  if (registro < 1 || registro > 22 || rut.slice(2, 8) === "000000" || rut.slice(8, 11) !== "001") {
    return { valido: false, motivo: "formato" };
  }
  const esperado = digitoVerificadorDeRut2(rut.slice(0, 11));
  return esperado !== 10 && esperado === Number(rut.slice(11)) ? { valido: true } : { valido: false, motivo: "digito_verificador" };
}
function digitoVerificadorDeCedula(numero2) {
  const siete = numero2.padStart(7, "0");
  let suma = 0;
  PESOS_DE_LA_CEDULA.forEach((peso, i) => {
    suma += Number(siete[i]) * peso;
  });
  return (10 - suma % 10) % 10;
}
function validarCedula2(valor) {
  const cedula = soloDigitos2(valor);
  if (cedula.length < 7 || cedula.length > 8) return { valido: false, motivo: "formato" };
  return digitoVerificadorDeCedula(cedula.slice(0, -1)) === Number(cedula.slice(-1)) ? { valido: true } : { valido: false, motivo: "digito_verificador" };
}
var URUGUAY = {
  codigo: "UY",
  monedaPorDefecto: "UYU",
  zonaHoraria: "America/Montevideo",
  tiposDeIdentificacion: ["RUT", "CI", "PASAPORTE", "OTRO"],
  tiposDeIdentificacionDeEmpresa: ["RUT"],
  tiposDeIdentificacionDeTercero: ["RUT", "CI", "PASAPORTE", "SIN_IDENTIFICAR"],
  ejemploDeIdentificacion: "21-100342-001-7",
  normalizarIdentificacion(valor) {
    return soloDigitos2(valor);
  },
  validarIdentificacion(tipo, valor) {
    switch (tipo) {
      case "RUT":
        return validarRut2(valor);
      case "CI":
        return validarCedula2(valor);
      case "PASAPORTE":
      case "OTRO":
        return algoEscrito(valor);
      default:
        return { valido: false, motivo: "tipo_no_admitido" };
    }
  },
  formatearIdentificacion(tipo, valor) {
    const digitos = soloDigitos2(valor);
    if (tipo === "RUT" && digitos.length === 12) {
      return `${digitos.slice(0, 2)}-${digitos.slice(2, 8)}-${digitos.slice(8, 11)}-${digitos.slice(11)}`;
    }
    if (tipo === "CI" && digitos.length >= 2) {
      return `${conPuntos(digitos.slice(0, -1))}-${digitos.slice(-1)}`;
    }
    return valor.trim();
  },
  // SUPUESTO: el régimen general (IRAE e IVA), la pequeña empresa del literal E del artículo 52
  // del Título 4 del Texto Ordenado 1996 —que paga IVA mínimo— y los dos monotributos, el común y
  // el social del MIDES. Falta que las confirme alguien que trabaje con comercios uruguayos.
  condicionesDelEmisor: ["regimen_general", "literal_e", "monotributo", "monotributo_mides"],
  condicionesDelReceptor: ["contribuyente", "consumidor_final", "exento"],
  condicionDelReceptorPorOmision: "consumidor_final",
  // Título 10 del Texto Ordenado 1996, artículo 18: tasa básica del 22 % y mínima del 10 %.
  alicuotas: { BASICA: "22.0000", MINIMA: "10.0000", EXENTO: "0.0000" },
  alicuotaGeneral: "BASICA",
  claseDelComprobante: () => null,
  tieneFiscoElectronico: false
};

// src/ar/factura-electronica.ts
var CONCEPTO_PRODUCTOS = 1;
var DOCUMENTO_CUIT = 80;
var DOCUMENTO_SIN_IDENTIFICAR = 99;
function idDeAlicuota(codigo) {
  return Number(codigo);
}
function fechaDelComprobante(fecha2) {
  const a\u00F1o = fecha2.getFullYear();
  const mes = String(fecha2.getMonth() + 1).padStart(2, "0");
  const dia = String(fecha2.getDate()).padStart(2, "0");
  return `${a\u00F1o}${mes}${dia}`;
}
function armarPedidoDeCae(datos) {
  const esC = datos.clase === "C";
  const total = Number(datos.total);
  const sinIdentificar = datos.identificacionDelReceptor === null;
  const quePide = datos.quePide ?? "factura";
  return {
    PtoVta: datos.puntoDeVenta,
    CbteTipo: TIPOS_DE_COMPROBANTE[datos.clase][quePide],
    Concepto: CONCEPTO_PRODUCTOS,
    DocTipo: sinIdentificar ? DOCUMENTO_SIN_IDENTIFICAR : DOCUMENTO_CUIT,
    // Con DocTipo 99 el número va en cero, no vacío: lo dice la validación 10015.
    DocNro: sinIdentificar ? 0 : Number(datos.identificacionDelReceptor),
    // Un comprobante por pedido. El lote existe en el contrato pero acá cada venta se autoriza
    // sola: agrupar ataría el destino de una venta al de otra, y el mostrador emite de a una.
    CbteDesde: datos.numero,
    CbteHasta: datos.numero,
    CbteFch: fechaDelComprobante(datos.emitidoEn),
    ImpTotal: total,
    ImpTotConc: 0,
    // En el C el neto es el total: no hay impuesto que separar.
    ImpNeto: esC ? total : Number(datos.neto),
    ImpOpEx: 0,
    // Percepciones e impuestos internos todavía no existen en el sistema, así que van en cero
    // porque son cero, no porque se los esté ignorando.
    ImpTrib: 0,
    ImpIVA: esC ? 0 : Number(datos.iva),
    MonId: datos.moneda,
    MonCotiz: Number(datos.cotizacion),
    CondicionIVAReceptorId: datos.condicionIvaDelReceptor,
    Iva: esC ? [] : datos.porAlicuota.map((parte) => ({
      Id: idDeAlicuota(parte.codigo),
      BaseImp: Number(parte.neto),
      Importe: Number(parte.iva)
    })),
    CbtesAsoc: datos.asociado === void 0 ? [] : [
      {
        // El tipo del asociado es el de **la factura** que se ajusta, no el de la nota.
        Tipo: TIPOS_DE_COMPROBANTE[datos.asociado.clase].factura,
        PtoVta: datos.asociado.puntoDeVenta,
        Nro: datos.asociado.numero
      }
    ]
  };
}
var URL_DEL_QR = "https://www.arca.gob.ar/fe/qr/";
function datosDelQr(datos) {
  const fecha2 = datos.emitidoEn;
  const enIso = `${String(fecha2.getFullYear())}-${String(fecha2.getMonth() + 1).padStart(2, "0")}-${String(fecha2.getDate()).padStart(2, "0")}`;
  return {
    ver: 1,
    fecha: enIso,
    cuit: Number(datos.cuitDelEmisor),
    ptoVta: datos.puntoDeVenta,
    tipoCmp: TIPOS_DE_COMPROBANTE[datos.clase][datos.quePide ?? "factura"],
    nroCmp: datos.numero,
    importe: Number(datos.total),
    moneda: datos.moneda,
    ctz: Number(datos.cotizacion),
    ...datos.identificacionDelReceptor === null ? {} : {
      tipoDocRec: DOCUMENTO_CUIT,
      nroDocRec: Number(datos.identificacionDelReceptor)
    },
    tipoCodAut: "E",
    codAut: Number(datos.cae)
  };
}
function textoDelQr(datos) {
  const json = JSON.stringify(datos);
  const enBase64 = typeof Buffer === "undefined" ? btoa(String.fromCodePoint(...new TextEncoder().encode(json))) : Buffer.from(json, "utf8").toString("base64");
  return `${URL_DEL_QR}?p=${enBase64}`;
}

// src/ar/contabilidad.ts
var PLAN_DE_CUENTAS_ARGENTINO = [
  // --- 1. Activo -------------------------------------------------------------------------
  { codigo: "1", nombre: "Activo", tipo: "activo", padre: null },
  { codigo: "1.1", nombre: "Activo corriente", tipo: "activo", padre: "1" },
  { codigo: "1.1.01", nombre: "Caja y bancos", tipo: "activo", padre: "1.1" },
  { codigo: "1.1.01.001", nombre: "Caja", tipo: "activo", padre: "1.1.01" },
  { codigo: "1.1.01.002", nombre: "Banco cuenta corriente", tipo: "activo", padre: "1.1.01" },
  // Lo cobrado con tarjeta todavía no es plata en la cuenta: es una promesa del sello, y por eso
  // no puede ir a caja ni a banco. Es la cuenta que E5 dejó pendiente de nombre.
  { codigo: "1.1.01.003", nombre: "Valores a depositar", tipo: "activo", padre: "1.1.01" },
  // Lo que salió de la cartera y el banco todavía no acreditó. **Existe porque depositar y
  // acreditar son dos momentos**: sin esta cuenta, la plata aparecería en el banco el día que la
  // boleta salió del mostrador.
  { codigo: "1.1.01.004", nombre: "Valores en tr\xE1nsito", tipo: "activo", padre: "1.1.01" },
  { codigo: "1.1.02", nombre: "Cr\xE9ditos por ventas", tipo: "activo", padre: "1.1" },
  { codigo: "1.1.02.001", nombre: "Deudores por ventas", tipo: "activo", padre: "1.1.02" },
  { codigo: "1.1.03", nombre: "Otros cr\xE9ditos", tipo: "activo", padre: "1.1" },
  { codigo: "1.1.03.001", nombre: "IVA cr\xE9dito fiscal", tipo: "activo", padre: "1.1.03" },
  { codigo: "1.1.03.002", nombre: "Anticipos a proveedores", tipo: "activo", padre: "1.1.03" },
  // Lo que un agente de recaudación le retuvo al comercio y el comercio va a computar contra su
  // propia declaración. Es un crédito, no un gasto: mandarlo a resultados sería regalarlo.
  { codigo: "1.1.03.003", nombre: "Percepciones sufridas", tipo: "activo", padre: "1.1.03" },
  { codigo: "1.1.04", nombre: "Bienes de cambio", tipo: "activo", padre: "1.1" },
  { codigo: "1.1.04.001", nombre: "Mercader\xEDas", tipo: "activo", padre: "1.1.04" },
  // --- 2. Pasivo -------------------------------------------------------------------------
  { codigo: "2", nombre: "Pasivo", tipo: "pasivo", padre: null },
  { codigo: "2.1", nombre: "Pasivo corriente", tipo: "pasivo", padre: "2" },
  { codigo: "2.1.01", nombre: "Deudas comerciales", tipo: "pasivo", padre: "2.1" },
  { codigo: "2.1.01.001", nombre: "Proveedores", tipo: "pasivo", padre: "2.1.01" },
  // Lo que llegó y todavía no facturaron. Lo que queda acá es la diferencia entre lo recibido y
  // lo facturado, que es el número que el cotejo de tres puntas busca.
  { codigo: "2.1.01.002", nombre: "Mercader\xEDas a facturar", tipo: "pasivo", padre: "2.1.01" },
  { codigo: "2.1.02", nombre: "Deudas fiscales", tipo: "pasivo", padre: "2.1" },
  { codigo: "2.1.02.001", nombre: "IVA d\xE9bito fiscal", tipo: "pasivo", padre: "2.1.02" },
  { codigo: "2.1.03", nombre: "Anticipos de clientes", tipo: "pasivo", padre: "2.1" },
  { codigo: "2.1.03.001", nombre: "Anticipos de clientes", tipo: "pasivo", padre: "2.1.03" },
  // --- 3. Patrimonio neto ----------------------------------------------------------------
  { codigo: "3", nombre: "Patrimonio neto", tipo: "patrimonio", padre: null },
  { codigo: "3.1", nombre: "Capital", tipo: "patrimonio", padre: "3" },
  { codigo: "3.1.01.001", nombre: "Capital social", tipo: "patrimonio", padre: "3.1" },
  // Lo que el dueño saca del cajón para sí, o pone de su bolsillo (E37). No es un gasto: un retiro
  // mandado a resultados haría que el comercio pareciera perder lo que el dueño se llevó.
  {
    codigo: "3.1.01.002",
    nombre: "Cuenta particular del due\xF1o",
    tipo: "patrimonio",
    padre: "3.1"
  },
  { codigo: "3.2", nombre: "Resultados", tipo: "patrimonio", padre: "3" },
  { codigo: "3.2.01.001", nombre: "Resultados acumulados", tipo: "patrimonio", padre: "3.2" },
  // Donde el cierre del ejercicio deja el resultado antes de acumularlo.
  { codigo: "3.2.01.002", nombre: "Resultado del ejercicio", tipo: "patrimonio", padre: "3.2" },
  // --- 4. Resultados positivos -----------------------------------------------------------
  { codigo: "4", nombre: "Resultados positivos", tipo: "resultado_positivo", padre: null },
  { codigo: "4.1", nombre: "Ventas", tipo: "resultado_positivo", padre: "4" },
  {
    codigo: "4.1.01.001",
    nombre: "Ventas de mercader\xEDas",
    tipo: "resultado_positivo",
    padre: "4.1"
  },
  // Se restan de las ventas al armar el resultado, pero viven aparte: un contador quiere ver
  // cuánto le devuelven, no una cifra de ventas ya neteada donde eso no se nota.
  {
    codigo: "4.1.01.002",
    nombre: "Devoluciones sobre ventas",
    tipo: "resultado_positivo",
    padre: "4.1"
  },
  { codigo: "4.2", nombre: "Otros ingresos", tipo: "resultado_positivo", padre: "4" },
  // El redondeo del efectivo al peso: unos centavos por venta que a fin de mes son plata.
  {
    codigo: "4.2.01.001",
    nombre: "Diferencias de redondeo",
    tipo: "resultado_positivo",
    padre: "4.2"
  },
  // Lo que entra al cajón y no es una venta ni lo pone el dueño: un reintegro, una propina del
  // flete que se cobró de más (E37).
  { codigo: "4.2.01.002", nombre: "Ingresos varios", tipo: "resultado_positivo", padre: "4.2" },
  // --- 5. Resultados negativos -----------------------------------------------------------
  { codigo: "5", nombre: "Resultados negativos", tipo: "resultado_negativo", padre: null },
  { codigo: "5.1", nombre: "Costo de ventas", tipo: "resultado_negativo", padre: "5" },
  {
    codigo: "5.1.01.001",
    nombre: "Costo de mercader\xEDas vendidas",
    tipo: "resultado_negativo",
    padre: "5.1"
  },
  { codigo: "5.2", nombre: "Gastos", tipo: "resultado_negativo", padre: "5" },
  {
    codigo: "5.2.01.001",
    nombre: "Gastos sobre compras",
    tipo: "resultado_negativo",
    padre: "5.2"
  },
  // La diferencia del arqueo. Se registra, nunca se ajusta en silencio (INV-09).
  { codigo: "5.2.02.001", nombre: "Diferencias de caja", tipo: "resultado_negativo", padre: "5.2" },
  // Lo que aparece o desaparece del estante y no se explica por una venta ni por una compra. Es
  // pérdida del período, salvo la existencia inicial: ver `ajuste_patrimonio`.
  {
    codigo: "5.2.02.002",
    nombre: "Diferencias de inventario",
    tipo: "resultado_negativo",
    padre: "5.2"
  },
  // Lo que sale del cajón para que el comercio funcione: la limpieza, el café, el cerrajero (E37).
  { codigo: "5.2.04.001", nombre: "Gastos generales", tipo: "resultado_negativo", padre: "5.2" },
  { codigo: "5.2.04.002", nombre: "Fletes y acarreos", tipo: "resultado_negativo", padre: "5.2" },
  // Lo que el proveedor cobra en la factura y no es ni mercadería ni IVA ni percepción:
  // impuestos internos, tasas municipales, sellos. Son gasto del período.
  {
    codigo: "5.2.03.001",
    nombre: "Impuestos, tasas y contribuciones",
    tipo: "resultado_negativo",
    padre: "5.2"
  }
];
var CUENTAS_QUE_LOS_MODULOS_NECESITAN = [
  "1.1.01.001",
  // caja
  "1.1.01.003",
  // valores a depositar
  "1.1.02.001",
  // deudores por ventas
  "1.1.03.001",
  // IVA crédito fiscal
  "1.1.04.001",
  // mercaderías
  "2.1.01.001",
  // proveedores
  "2.1.02.001",
  // IVA débito fiscal
  "4.1.01.001",
  // ventas
  "5.1.01.001",
  // costo de mercaderías vendidas
  "2.1.01.002",
  // mercaderías a facturar
  "4.1.01.002",
  // devoluciones sobre ventas
  "5.2.02.001",
  // diferencias de caja
  "2.1.03.001",
  // anticipos de clientes
  "1.1.03.003",
  // percepciones sufridas
  "5.2.03.001",
  // impuestos, tasas y contribuciones
  "1.1.01.002",
  // banco cuenta corriente
  "1.1.01.004",
  // valores en tránsito
  "5.2.02.002",
  // diferencias de inventario
  "3.2.01.001",
  // resultados acumulados
  "5.2.04.001",
  // gastos generales
  "5.2.04.002",
  // fletes y acarreos
  "3.1.01.002",
  // cuenta particular del dueño
  "4.2.01.002"
  // ingresos varios
];

// src/ar/libro-de-iva.ts
var MONEDA_PESOS = "PES";
var CAMBIO_EN_PESOS = "0001000000";
var MONTO_QUE_OBLIGA_A_IDENTIFICAR = "20932.00";
var CONSUMIDOR_FINAL = "CONSUMIDOR FINAL";
var DOCUMENTO_SIN_IDENTIFICAR2 = 99;
var CODIGOS_DE_OPERACION = {
  noCorresponde: " ",
  exenta: "E",
  noGravado: "N",
  noAlcanzado: "A",
  canje: "C",
  exportacionAlExterior: "X",
  exportacionAZonaFranca: "Z",
  devolucionATuristas: "D",
  reintegro1043: "T"
};
function texto(valor, largo) {
  return valor.slice(0, largo).padEnd(largo, " ");
}
function numero(valor, largo) {
  return String(valor).padStart(largo, "0").slice(-largo);
}
function importe(valor) {
  const negativo = valor.trimStart().startsWith("-");
  const [entera = "0", decimales = ""] = valor.replace("-", "").trim().split(".");
  const centavos = decimales.padEnd(2, "0").slice(0, 2);
  const enteros = negativo ? `-${entera.padStart(12, "0").slice(-12)}` : entera.padStart(13, "0").slice(-13);
  return `${enteros}${centavos}`;
}
function fecha(iso) {
  return iso.slice(0, 10).replaceAll("-", "");
}
var LARGO_DE_VENTAS_CBTE = 266;
var LARGO_DE_VENTAS_ALICUOTAS = 62;
var LARGO_DE_COMPRAS_CBTE = 325;
var LARGO_DE_COMPRAS_ALICUOTAS = 84;
var LARGO_DE_VENTAS_ANULADOS = 44;
function renglonDeVenta(datos) {
  const renglon = [
    fecha(datos.fecha),
    //                                 1  ·  1–8    · 8
    numero(datos.tipo, 3),
    //                              2  ·  9–11   · 3
    numero(datos.puntoDeVenta, 5),
    //                      3  ·  12–16  · 5
    numero(datos.numero, 20),
    //                           4  ·  17–36  · 20
    numero(datos.numero, 20),
    //                           5  ·  37–56  · 20
    numero(datos.codigoDeDocumento, 2),
    //                 6  ·  57–58  · 2
    numero(datos.identificacion, 20),
    //                   7  ·  59–78  · 20
    texto(datos.nombre, 30),
    //                            8  ·  79–108 · 30
    importe(datos.total),
    //                               9  ·  109–123
    importe(datos.noGravado),
    //                          10  ·  124–138
    importe(datos.percepcionANoCategorizados),
    //         11  ·  139–153
    importe(datos.exento),
    //                             12  ·  154–168
    importe(datos.percepcionesNacionales),
    //             13  ·  169–183
    importe(datos.percepcionesDeIngresosBrutos),
    //       14  ·  184–198
    importe(datos.percepcionesMunicipales),
    //            15  ·  199–213
    importe(datos.impuestosInternos),
    //                  16  ·  214–228
    MONEDA_PESOS,
    //                                      17  ·  229–231 · 3
    CAMBIO_EN_PESOS,
    //                                   18  ·  232–241 · 10
    numero(datos.cantidadDeAlicuotas, 1),
    //              19  ·  242     · 1
    texto(datos.codigoDeOperacion, 1),
    //                 20  ·  243     · 1
    importe(datos.otrosTributos),
    //                      21  ·  244–258
    texto("", 8)
    //                                      22  ·  259–266 · 8
  ].join("");
  return verificar(renglon, LARGO_DE_VENTAS_CBTE, "ventas");
}
function renglonDeAlicuotaDeVenta(datos) {
  const renglon = [
    numero(datos.tipo, 3),
    //                    1 · 1–3
    numero(datos.puntoDeVenta, 5),
    //            2 · 4–8
    numero(datos.numero, 20),
    //                 3 · 9–28
    importe(datos.neto),
    //                      4 · 29–43
    numero(datos.codigoDeAlicuota, 4),
    //        5 · 44–47
    importe(datos.iva)
    //                       6 · 48–62
  ].join("");
  return verificar(renglon, LARGO_DE_VENTAS_ALICUOTAS, "al\xEDcuotas de ventas");
}
function renglonDeCompra(datos) {
  const renglon = [
    fecha(datos.fecha),
    //                                 1  ·  1–8
    numero(datos.tipo, 3),
    //                              2  ·  9–11
    numero(datos.puntoDeVenta, 5),
    //                      3  ·  12–16
    numero(datos.numero, 20),
    //                           4  ·  17–36
    texto("", 16),
    //                                      5  ·  37–52  · despacho de importación
    numero(datos.codigoDeDocumento, 2),
    //                 6  ·  53–54
    numero(datos.identificacion, 20),
    //                   7  ·  55–74
    texto(datos.nombre, 30),
    //                            8  ·  75–104
    importe(datos.total),
    //                               9  ·  105–119
    importe(datos.noGravado),
    //                          10  ·  120–134
    importe(datos.exento),
    //                             11  ·  135–149
    importe(datos.percepcionesDeIva),
    //                  12  ·  150–164
    importe(datos.percepcionesNacionales),
    //             13  ·  165–179
    importe(datos.percepcionesDeIngresosBrutos),
    //       14  ·  180–194
    importe(datos.percepcionesMunicipales),
    //            15  ·  195–209
    importe(datos.impuestosInternos),
    //                  16  ·  210–224
    MONEDA_PESOS,
    //                                      17  ·  225–227
    CAMBIO_EN_PESOS,
    //                                   18  ·  228–237
    numero(datos.cantidadDeAlicuotas, 1),
    //              19  ·  238
    texto(datos.codigoDeOperacion, 1),
    //                 20  ·  239
    importe(datos.creditoFiscalComputable),
    //            21  ·  240–254
    importe(datos.otrosTributos),
    //                      22  ·  255–269
    numero(0, 11),
    //                                     23  ·  270–280 · CUIT emisor/corredor
    texto("", 30),
    //                                     24  ·  281–310 · su denominación
    importe("0")
    //                                      25  ·  311–325 · IVA comisión
  ].join("");
  return verificar(renglon, LARGO_DE_COMPRAS_CBTE, "compras");
}
function renglonDeAlicuotaDeCompra(datos) {
  const renglon = [
    numero(datos.tipo, 3),
    //                    1 · 1–3
    numero(datos.puntoDeVenta, 5),
    //            2 · 4–8
    numero(datos.numero, 20),
    //                 3 · 9–28
    numero(datos.codigoDeDocumento, 2),
    //       4 · 29–30
    numero(datos.identificacion, 20),
    //         5 · 31–50
    importe(datos.neto),
    //                      6 · 51–65
    numero(datos.codigoDeAlicuota, 4),
    //        7 · 66–69
    importe(datos.iva)
    //                       8 · 70–84
  ].join("");
  return verificar(renglon, LARGO_DE_COMPRAS_ALICUOTAS, "al\xEDcuotas de compras");
}
function renglonDeAnulado(datos) {
  const renglon = [
    fecha(datos.fecha),
    //                       1 · 1–8
    numero(datos.tipo, 3),
    //                    2 · 9–11
    numero(datos.puntoDeVenta, 5),
    //            3 · 12–16
    numero(datos.numero, 20),
    //                 4 · 17–36
    fecha(datos.fechaDeAnulacion)
    //            5 · 37–44
  ].join("");
  return verificar(renglon, LARGO_DE_VENTAS_ANULADOS, "anulados");
}
function cuantasAlicuotasDeclara(esVenta, clase, alicuotas) {
  if (!esVenta && clase !== "A") return 0;
  return Math.max(alicuotas, 1);
}
function verificar(renglon, largo, cual) {
  if (renglon.length !== largo) {
    throw new Error(
      `El rengl\xF3n de ${cual} mide ${String(renglon.length)} y tiene que medir ${String(largo)}`
    );
  }
  return renglon;
}

// src/index.ts
var PAQUETES = new Map(
  [ARGENTINA, CHILE, PARAGUAY, URUGUAY].map((paquete) => [paquete.codigo, paquete])
);
function paqueteDePais(codigo) {
  return PAQUETES.get(codigo.toUpperCase());
}
function paisesSoportados() {
  return [...PAQUETES.keys()];
}
function tasaDeAlicuotaEnCualquierPais(codigo) {
  return tasaDeAlicuota(PAQUETES.values(), codigo);
}
export {
  ALICUOTAS_DE_IVA,
  ALICUOTA_GENERAL,
  ARGENTINA,
  CAMBIO_EN_PESOS,
  CHILE,
  CODIGOS_DE_ALICUOTA,
  CODIGOS_DE_OPERACION,
  CONCEPTO_PRODUCTOS,
  CONDICION_IVA_DEL_RECEPTOR,
  CONSUMIDOR_FINAL,
  CUENTAS_QUE_LOS_MODULOS_NECESITAN,
  DATO_DE_IVA_CONTENIDO,
  DATO_DE_OTROS_IMPUESTOS,
  DOCUMENTO_CUIT,
  DOCUMENTO_SIN_IDENTIFICAR,
  DOCUMENTO_SIN_IDENTIFICAR2 as DOCUMENTO_SIN_IDENTIFICAR_EN_EL_LIBRO,
  IDENTIFICACION_ARGENTINA,
  LARGO_DE_COMPRAS_ALICUOTAS,
  LARGO_DE_COMPRAS_CBTE,
  LARGO_DE_VENTAS_ALICUOTAS,
  LARGO_DE_VENTAS_ANULADOS,
  LARGO_DE_VENTAS_CBTE,
  LEYENDA_DEL_EMISOR,
  LEYENDA_DEL_RECEPTOR,
  LEYENDA_PARA_MONOTRIBUTISTA,
  MONEDA_PESOS,
  MONTO_QUE_OBLIGA_A_IDENTIFICAR,
  PARAGUAY,
  PLAN_DE_CUENTAS_ARGENTINO,
  TIPOS_DE_COMPROBANTE,
  TITULO_DE_TRANSPARENCIA_FISCAL,
  URL_DEL_QR,
  URUGUAY,
  alicuotaDe,
  alicuotaQueCorresponde,
  armarPedidoDeCae,
  claseQueCorresponde,
  cuantasAlicuotasDeclara,
  datosDelQr,
  digitoVerificadorDeCedula as digitoVerificadorDeCedulaUruguaya,
  digitoVerificadorDeCuit,
  digitoVerificadorDeRuc,
  digitoVerificadorDeRut as digitoVerificadorDeRutChileno,
  digitoVerificadorDeRut2 as digitoVerificadorDeRutUruguayo,
  discriminaIva,
  esAlicuotaDelPais,
  esCodigoDeAlicuota,
  fechaDelComprobante,
  fecha as fechaDelLibro,
  idDeAlicuota,
  importe as importeDelLibro,
  llevaLeyendaDeMonotributo,
  llevaTransparenciaFiscal,
  numeroDeComprobante,
  numero as numeroDelLibro,
  paisesSoportados,
  paqueteDePais,
  renglonDeAlicuotaDeCompra,
  renglonDeAlicuotaDeVenta,
  renglonDeAnulado,
  renglonDeCompra,
  renglonDeVenta,
  tasaDeAlicuotaDelPais,
  tasaDeAlicuotaEnCualquierPais,
  texto as textoDelLibro,
  textoDelQr,
  validarCuit,
  validarDni
};
//# sourceMappingURL=index.js.map